/**
 * useFirebaseAuthRestore Hook
 *
 * Checks for a persisted Firebase session on app startup BEFORE the MSAL gate.
 * Firebase stores refresh tokens in IndexedDB, which survives tab close and browser restart.
 * This check resolves in ~50-100ms (IndexedDB read), allowing returning users to bypass
 * the MSAL login redirect entirely.
 *
 * Flow:
 * 1. Initialize Firebase app (idempotent — safe to call if already initialized)
 * 2. Listen for first onAuthStateChanged callback
 * 3. If user found: initialize Firestore/RTDB/Messaging, mark authSource='firebase_restored'
 * 4. If user null: mark authSource='none' (fall through to MSAL gate)
 * 5. Always set isAuthChecking=false when done
 *
 * Called once in AuthenticatedLayout, before any other auth logic runs.
 */

import { useEffect, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager, setLogLevel } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getMessaging, isSupported } from 'firebase/messaging';
import { useFirebaseStore } from '../store/firebaseStore';
import { firebaseConfig } from '../config';
import { logger } from '../utils/logger';

/** Safety timeout (ms) in case onAuthStateChanged never fires */
const AUTH_CHECK_TIMEOUT = 3000;

export const useFirebaseAuthRestore = () => {
  const {
    setDb,
    setRtdb,
    setMessaging,
    setIsConnected,
    setAuthSource,
    setIsAuthChecking,
    isConnected,
  } = useFirebaseStore();

  const hasRun = useRef(false);

  useEffect(() => {
    // Only run once per session
    if (hasRun.current) return;
    hasRun.current = true;

    // If Firebase services are already connected (e.g., fast page refresh),
    // skip the check entirely
    if (isConnected) {
      setIsAuthChecking(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let resolved = false;

    const resolve = () => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };

    const checkPersistedAuth = async () => {
      try {
        // Suppress Firestore debug logs during initialization
        setLogLevel('error');

        // Initialize Firebase app (idempotent)
        let app;
        if (getApps().length === 0) {
          app = initializeApp(firebaseConfig);
        } else {
          app = getApps()[0];
        }

        const auth = getAuth(app);

        // Listen for the first auth state callback
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (resolved) return;

          if (user) {
            logger.log('[AuthRestore] Found persisted Firebase session for:', user.email || user.uid);

            try {
              // Initialize Firestore with persistent cache (same pattern as useFirebaseInit)
              let db;
              try {
                db = initializeFirestore(app, {
                  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
                });
              } catch (error) {
                const code = error && typeof error === 'object' && 'code' in error ? (error as { code?: unknown }).code : undefined;
                const message = error instanceof Error ? error.message : String(error);
                if (code === 'failed-precondition' || message.includes('already been called')) {
                  db = getFirestore(app);
                } else {
                  throw error;
                }
              }

              // Initialize RTDB
              const rtdb = getDatabase(app);

              // Initialize Messaging (if supported)
              let messaging = null;
              try {
                if (await isSupported()) {
                  messaging = getMessaging(app);
                }
              } catch {
                // Non-fatal — messaging is optional
              }

              // Set Firebase services in store
              setDb(db);
              setRtdb(rtdb);
              setMessaging(messaging);
              setIsConnected(true);
              setAuthSource('firebase_restored');
              logger.log('[AuthRestore] Firebase services initialized from persisted session');
            } catch (error) {
              logger.error('[AuthRestore] Failed to initialize Firebase services:', error);
              setAuthSource('none');
            }
          } else {
            logger.log('[AuthRestore] No persisted Firebase session found');
            setAuthSource('none');
          }

          setIsAuthChecking(false);
          resolve();
        });

        // Safety timeout — if onAuthStateChanged never fires, unblock the auth flow
        timeoutId = setTimeout(() => {
          if (!resolved) {
            logger.warn('[AuthRestore] Auth check timed out after', AUTH_CHECK_TIMEOUT, 'ms');
            setAuthSource('none');
            setIsAuthChecking(false);
            resolve();
          }
        }, AUTH_CHECK_TIMEOUT);
      } catch (error) {
        logger.error('[AuthRestore] Error during persistence check:', error);
        setAuthSource('none');
        setIsAuthChecking(false);
        resolve();
      }
    };

    checkPersistedAuth();

    return () => {
      resolve();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount — deps intentionally empty
};
